<template>
    <v-layout wrap>
        <v-flex xs12 class="d-flex align-center justify-space-between pb-0">
            <h2 class="text-h6">
                Histórico
                <span v-if="filteredHistory.length != history.length" class="text-subtitle-2 grey--text">{{ filteredHistory.length }} de {{ history.length }}</span>
            </h2>
            <RefreshTimer v-if="refresh" v-bind="refresh" />
        </v-flex>
        <v-flex xs12>
            <v-data-table :headers="headers" :items="filteredHistory" :item-class="rowClass" :items-per-page="-1" class="elevation-2" hide-default-footer dense disable-sort :no-data-text="history.length > 0 ? 'Nenhum carregamento encontrado com os filtros' : 'Nenhum carregamento finalizado no período'">
                <template v-slot:[`item.time`]="{ item }">{{ item.dateTime ? item.dateTime.substr(11, 5) : "-" }}</template>
                <template v-slot:[`item.transactionId`]="{ item }">{{ item.transactionId ? `#${item.transactionId}` : "-" }}</template>
                <template v-slot:[`item.charger`]="{ item }">
                    <a v-if="item.charger && item.plug" :href="format.nowUrl(item.charger, item.plug)" target="_blank" rel="noopener" class="charger-link" title="Abrir carregador em nova aba">
                        <strong>{{ item.charger.name }}</strong> ({{ item.plug }})
                    </a>
                    <strong v-else>{{ item.charger ? item.charger.name : "-" }}</strong>
                </template>
                <template v-slot:[`item.point`]="{ item }">
                    <div class="text-truncate cell-point">{{ item.charger && item.charger.point ? item.charger.point.name : "-" }}</div>
                </template>
                <template v-slot:[`item.user`]="{ item }">
                    <v-icon small class="mr-1">{{ isPaid(item) ? "credit_card" : item.car && item.car.nfc ? "nfc" : "smartphone" }}</v-icon>
                    <span :title="item.user ? item.user.email : undefined">{{ userLabel(item) }}</span>
                </template>
                <template v-slot:[`item.consumption`]="{ item }">
                    <v-icon v-if="failed(item)" small color="red" class="mr-1" title="Carregamento finalizado sem energia entregue">error</v-icon>
                    <span>{{ item.car ? `${Number(item.car.consumption).toFixed(2)} kWh` : "-" }}</span>
                </template>
                <template v-slot:[`item.minutesCharger`]="{ item }">{{ item.car ? format.time(item.car.minutesCharger) : "-" }}</template>
                <template v-slot:[`item.minutesConnected`]="{ item }">{{ item.car ? format.time(item.car.minutesConnected) : "-" }}</template>
                <template v-slot:[`item.maximumPower`]="{ item }">{{ item.car && item.car.maximumPower ? `${format.kW(item.car.maximumPower)} kW` : "-" }}</template>
                <template v-slot:[`item.battery`]="{ item }">{{ item.car ? format.battery(item.car.percentStart, item.car.percent) : "-" }}</template>
                <template v-slot:[`item.status`]="{ item }">
                    <v-chip x-small label :color="historyStatus(item.status).color" text-color="white">{{ historyStatus(item.status).text }}</v-chip>
                </template>
                <template v-slot:[`item.payment`]="{ item }">
                    <a v-if="item.payment" class="text-no-wrap payment-link" :title="`Pagamento #${item.payment.id} · ${item.payment.operator} · clique para gerenciar`" @click="$emit('open-payment', item.payment.id)">
                        {{ money(item.payment.value) }} <span class="caption grey--text">{{ paymentStatusLabel(item.payment.status) }}</span>
                    </a>
                    <span v-else>{{ isPaid(item) ? "?" : "-" }}</span>
                </template>
                <template v-slot:[`item.alerts`]="{ item }">
                    <v-icon v-for="alert in paymentAlerts(item)" :key="alert.key" small class="mr-1" :color="alert.critical ? 'red' : 'orange darken-2'" :title="alert.text">
                        {{ alert.critical ? "credit_card_off" : "payments" }}
                    </v-icon>
                    <v-btn v-if="receiptPdf.canIssue(item.status)" x-small icon class="receipt-btn" title="Gerar recibo do carregamento (PDF)" @click="printReceipt(item)">
                        <v-icon small>print</v-icon>
                    </v-btn>
                </template>
            </v-data-table>
        </v-flex>
    </v-layout>
</template>

<script>
import RefreshTimer from "@/components/shared/RefreshTimer";

import { money, paymentStatusLabel } from "@/helpers/paymentAlerts";
import receiptPdf from "@/pdf/receipt";

import { historyFailed, historyPaymentAlerts, isPaid } from "./shared/alerts";
import { matchHistory } from "./shared/filters";
import format from "./shared/format";
import { historyStatus } from "./shared/history";

export default {
    name: "HistoryArea",

    components: { RefreshTimer },

    props: {
        history: { type: Array, required: true },
        filters: { type: Object, required: true },
        refresh: { type: Object, default: null },
    },

    data() {
        return {
            format,
            receiptPdf,
        };
    },

    methods: {
        historyStatus,
        money,
        paymentStatusLabel,
        isPaid,
        printReceipt(item) {
            receiptPdf.download(receiptPdf.fromHistory(item, item.payment), item.transactionId);
        },
        paymentAlerts(item) {
            return historyPaymentAlerts(item);
        },
        failed(item) {
            return historyFailed(item);
        },
        rowClass(item) {
            if (this.paymentAlerts(item).some((a) => a.critical)) return "history-critical";
            return this.failed(item) ? "history-failed" : "";
        },
        userLabel(item) {
            if (item.user) return format.shortEmail(item.user.email);
            return item.car && item.car.nfc ? `NFC ${item.car.nfc}` : "-";
        },
    },

    computed: {
        filteredHistory() {
            return this.history.filter((item) => matchHistory(item, this.filters));
        },
        headers() {
            return [
                { text: "Início", value: "time", align: "center" },
                { text: "Transação", value: "transactionId", align: "center" },
                { text: "Carregador", value: "charger", align: "center" },
                { text: "Local", value: "point", align: "start" },
                { text: "Usuário", value: "user", align: "start" },
                { text: "Energia", value: "consumption", align: "center" },
                { text: "Carregando", value: "minutesCharger", align: "center" },
                { text: "Conectado", value: "minutesConnected", align: "center" },
                { text: "Pot. máxima", value: "maximumPower", align: "center" },
                { text: "Bateria", value: "battery", align: "center" },
                { text: "Status", value: "status", align: "center" },
                { text: "Pagamento", value: "payment", align: "center" },
                { text: "Alertas", value: "alerts", align: "start" },
            ];
        },
    },
};
</script>

<style scoped>
.v-data-table >>> .history-failed {
    background-color: rgba(244, 67, 54, 0.12);
}

.v-data-table >>> .history-critical td:first-child {
    box-shadow: inset 4px 0 0 #b71c1c;
}

.v-data-table >>> .history-critical {
    background-color: rgba(244, 67, 54, 0.12);
}

.charger-link {
    color: inherit;
    text-decoration: none;
}

.charger-link:hover {
    text-decoration: underline;
}

.payment-link {
    color: inherit;
}

.payment-link:hover {
    text-decoration: underline;
}

.receipt-btn {
    opacity: 0.5;
}

.receipt-btn:hover {
    opacity: 1;
}

.cell-point {
    max-width: 260px;
}
</style>
